import {
  ConfigurationType,
  LevelType,
  SidebarSectionType,
  StepTipType,
  ZoneType
} from 'data/types';
import { isConditionedSpace } from 'utils/conditioned-spaces';
import { collectPtacRooms } from 'utils/ptac-configuration';
import { PtacStepTextResolver } from 'utils/ptac-configuration-sections-types';
import { buildZoneStepSections } from 'utils/zone-step-sections';

export const PTAC_ZONES_TEXT_ID = 'ptac_zones';
export const PTAC_UNITS_TEXT_ID = 'ptac_units';
export const PTAC_UNITS_STEP_ID = 'air_handlers';
export const PTAC_UNITS_PANEL_ID = 'ptac-units-panel';
export const PTAC_UNIT_CARD_ID = 'new-ptac-unit';

export const PTAC_NO_ROOMS_REASON =
  'Add at least one conditioned room on Layout & Sizing before creating zones.';
const NO_ZONES_REASON = 'Add zones before placing PTAC units';

function unitsWithRooms(configuration: ConfigurationType | undefined) {
  return (configuration?.condensers ?? []).filter((unit) =>
    unit.zones.some((zone) => zone.zoneItems.length > 0)
  );
}

export function ptacUnitsPlacedCount(
  configuration: ConfigurationType | undefined
): { placed: number; total: number } {
  const units = unitsWithRooms(configuration);
  return {
    placed: units.filter((unit) => unit.position !== undefined).length,
    total: units.length
  };
}

export function ptacUnitsPlacedSummary(
  configuration: ConfigurationType | undefined
): string | undefined {
  const { placed, total } = ptacUnitsPlacedCount(configuration);
  return total > 0 ? `${placed} of ${total} placed` : undefined;
}

export function ptacUnitsStepComplete(
  configuration: ConfigurationType | undefined
): boolean {
  const { placed, total } = ptacUnitsPlacedCount(configuration);
  return total > 0 && placed === total;
}

export function ptacUnitsStepCompleteForLevel(
  level: LevelType,
  configuration: ConfigurationType | undefined
): boolean {
  if (!configuration) return false;

  const levelSpaceIds = new Set(
    level.sections
      .flatMap((section) => section.spaces)
      .filter(isConditionedSpace)
      .map((space) => space.uniqueId)
  );
  if (levelSpaceIds.size === 0) return true;

  const unitsOnLevel = unitsWithRooms(configuration).filter((unit) =>
    unit.zones.some((zone) =>
      zone.zoneItems.some((item) => levelSpaceIds.has(item.structureItemId))
    )
  );
  return (
    unitsOnLevel.length > 0 &&
    unitsOnLevel.every((unit) => unit.position !== undefined)
  );
}

export function ptacUnitsMissingReason(
  configuration: ConfigurationType | undefined
): string | undefined {
  const { placed, total } = ptacUnitsPlacedCount(configuration);
  if (total === 0) return NO_ZONES_REASON;
  if (placed >= total) return undefined;
  return `Place a PTAC unit in all zones (${placed}/${total})`;
}

function ptacZoneTips(
  getStepText: PtacStepTextResolver,
  firstUnzonedRoomHighlightId: string
): StepTipType[] {
  return [
    {
      targets: [firstUnzonedRoomHighlightId],
      title: getStepText(
        PTAC_ZONES_TEXT_ID,
        'tip_1_title',
        'Group rooms into zones'
      ),
      description: getStepText(
        PTAC_ZONES_TEXT_ID,
        'tip_1_description',
        'Click rooms on the floor plan to add them to the current zone. Rooms in a zone share one PTAC unit, which goes through the outside wall of the first room you pick.'
      )
    },
    {
      targets: ['step-accordion-zones'],
      title: getStepText(
        PTAC_ZONES_TEXT_ID,
        'tip_2_title',
        'Manage your zones here'
      ),
      description: getStepText(
        PTAC_ZONES_TEXT_ID,
        'tip_2_description',
        'This panel lists each zone and its rooms. Click a zone to make it active, drag a room to another zone, or remove rooms you added by mistake.'
      )
    }
  ];
}

function ptacUnitTips(
  getStepText: PtacStepTextResolver,
  firstZoneHighlightId: string
): StepTipType[] {
  return [
    {
      targets: [PTAC_UNIT_CARD_ID, firstZoneHighlightId],
      title: getStepText(
        PTAC_UNITS_TEXT_ID,
        'tip_1_title',
        'Place your PTAC units'
      ),
      description: getStepText(
        PTAC_UNITS_TEXT_ID,
        'tip_1_description',
        "Drag the PTAC unit card onto a zone's room on the floor plan. It mounts through the middle of that room's outside wall; drag it along the wall to move it."
      )
    },
    {
      targets: [PTAC_UNITS_PANEL_ID],
      title: getStepText(PTAC_UNITS_TEXT_ID, 'tip_2_title', 'Sizes come later'),
      description: getStepText(
        PTAC_UNITS_TEXT_ID,
        'tip_2_description',
        "Units are placeholders here — you pick each unit's size and model on the Choose Systems page. Generate, in the configuration tabs above the plan, adds a design with one unit per room for you."
      )
    }
  ];
}

function zonesOf(configuration: ConfigurationType | undefined): ZoneType[] {
  return configuration?.condensers.flatMap((unit) => unit.zones) ?? [];
}

function ptacZoneSections(
  configuration: ConfigurationType | undefined,
  structure: LevelType[],
  getStepText: PtacStepTextResolver
): SidebarSectionType[] {
  const rooms = collectPtacRooms(structure);
  const zones = zonesOf(configuration);
  const zonedRoomIds = new Set(
    zones.flatMap((zone) => zone.zoneItems.map((item) => item.structureItemId))
  );
  const firstUnzonedRoom = rooms.find(
    (room) => !zonedRoomIds.has(room.space.uniqueId)
  );
  return buildZoneStepSections({
    zonesWithItems: zones.filter((zone) => zone.zoneItems.length > 0),
    currentEmptyZone: zones.find((zone) => zone.zoneItems.length === 0),
    hasUnzonedSpaces: firstUnzonedRoom !== undefined,
    canStartZones: rooms.length > 0,
    cannotStartReason: PTAC_NO_ROOMS_REASON,
    allowNewZone: true,
    tips: ptacZoneTips(
      getStepText,
      firstUnzonedRoom
        ? `grid-space-${firstUnzonedRoom.space.uniqueId}`
        : 'structure-grid'
    )
  });
}

function ptacUnitsSection(
  configuration: ConfigurationType | undefined,
  getStepText: PtacStepTextResolver
): SidebarSectionType {
  const { placed, total } = ptacUnitsPlacedCount(configuration);
  const complete = ptacUnitsStepComplete(configuration);
  const title = `${getStepText(
    PTAC_UNITS_TEXT_ID,
    'title',
    'Place PTAC Units'
  )} (${placed}/${total})`;
  const description = getStepText(
    PTAC_UNITS_TEXT_ID,
    'description',
    'Drag a PTAC unit onto each zone.'
  );
  const firstZone = zonesOf(configuration).find(
    (zone) => zone.zoneItems.length > 0
  );

  return {
    id: PTAC_UNITS_STEP_ID,
    title,
    progressBarLabel: 'PTAC Units',
    description,
    requirementsMet: complete,
    requirementsMissingReason: ptacUnitsMissingReason(configuration),
    required: false,
    step_code: PTAC_UNITS_STEP_ID,
    steps: [
      {
        id: 'place_ptac_units',
        title,
        description,
        status: complete ? 'complete' : 'incomplete',
        tips: ptacUnitTips(
          getStepText,
          firstZone ? `zone-outline-${firstZone.uniqueId}` : 'structure-grid'
        )
      }
    ]
  };
}

export function generatePtacConfigurationSections(
  configuration: ConfigurationType | undefined,
  structure: LevelType[],
  getStepText: PtacStepTextResolver
): SidebarSectionType[] {
  return [
    ...ptacZoneSections(configuration, structure, getStepText),
    ptacUnitsSection(configuration, getStepText)
  ];
}
